import { register } from 'swiper/element/bundle'
register()

class Carousel extends HTMLElement {
  constructor() {
    super()
    // Add a class to hide the carousel during initialization
    this.classList.add('carousel-initializing')
  }

  connectedCallback() {
    const swiperEl = this.querySelector('swiper-container')
    const cardsSpacing = Number(this.dataset.cardsSpacing || 24)
    const progressBar = this.querySelector('.swiper-pagination-progress')
    const loadMoreButton = this.querySelector('#load-more-cards')
    const cardsContainer = this.querySelector('#cards-container')
    const pagination = this.dataset.pagination
    const paginationEl = this.querySelector('.swiper-pagination')
    const autoGap = this.dataset.autoGap
    const prevButton = this.querySelector('[data-carousel-prev-button]')
    const nextButton = this.querySelector('[data-carousel-next-button]')
    const cardType = this.dataset.cardType

    // Handle load more functionality
    if (loadMoreButton && cardsContainer) {
      // Calculate the height of one set of cards
      const calculateSetHeight = () => {
        const firstSet = Array.from(cardsContainer.children).slice(
          0,
          parseInt(loadMoreButton.dataset.cardsPerPage)
        )
        return firstSet.reduce((total, card) => {
          const style = window.getComputedStyle(card)
          const margin = parseFloat(style.marginTop) + parseFloat(style.marginBottom)
          return total + card.scrollHeight + margin
        }, 0)
      }

      loadMoreButton.addEventListener('click', () => {
        const totalCards = parseInt(loadMoreButton.dataset.totalCards)
        const cardsPerPage = parseInt(loadMoreButton.dataset.cardsPerPage)
        const currentOffset = parseInt(loadMoreButton.dataset.currentOffset)

        // Get the next set of cards
        const nextCards = Array.from(cardsContainer.children).slice(
          currentOffset,
          currentOffset + cardsPerPage
        )

        // Calculate and set the height for the next set
        const setHeight = calculateSetHeight()
        nextCards.forEach(card => {
          card.style.maxHeight = `${setHeight}px`
        })

        // Update the current offset
        loadMoreButton.dataset.currentOffset = currentOffset + cardsPerPage

        // Hide the load more button if we've shown all cards
        if (currentOffset + cardsPerPage >= totalCards) {
          loadMoreButton.style.display = 'none'
        }
      })
    }

    // Set up basic Swiper configuration
    if (swiperEl) {
      swiperEl.swiper.el.style.overflow = 'visible'
      // Remove initialization class once everything is set up
      requestAnimationFrame(() => {
        this.classList.remove('carousel-initializing')
        this.classList.add('initialized')
      })
      if (!autoGap) {
        swiperEl.swiper.wrapperEl.style.gap = `${cardsSpacing / 16}rem`
      }
      // Add data attributes to slides
      const slides = swiperEl.querySelectorAll('swiper-slide')
      slides.forEach((slide, index) => {
        slide.setAttribute('data-slide-index', index)
      })

      // Configure Swiper parameters
      swiperEl.swiper.params = {
        ...swiperEl.swiper.params,
        loop: swiperEl.swiper.slides.length > 4 || swiperEl.swiper.params.slidesPerView !== 'auto',
        spaceBetween: cardsSpacing,
        grabCursor: true,
        allowTouchMove: true,
        mousewheel: {
          forceToAxis: true,
          thresholdDelta: 50,
          thresholdTime: 300,
          releaseOnEdges: true,
        },
      }
      swiperEl.swiper.update() // Update Swiper with new parameters
    }

    if (prevButton) {
      if (
        (swiperEl.swiper.slides.length <= 4 && swiperEl.swiper.params.slidesPerView === 'auto') ||
        swiperEl.swiper.slides.length === 1
      ) {
        prevButton.classList.add('hidden')
      } else if (cardType === 'image-card' && swiperEl.swiper.slides.length <= 1) {
        prevButton.classList.add('hidden')
      } else {
        prevButton.classList.remove('hidden')
      }
      prevButton.addEventListener('click', () => {
        swiperEl.swiper.slidePrev()
      })
    }

    if (nextButton) {
      if (swiperEl.swiper.slides.length <= 4 && swiperEl.swiper.params.slidesPerView === 'auto') {
        nextButton.classList.add('hidden')
      } else if (cardType === 'image-card' && swiperEl.swiper.slides.length <= 1) {
        nextButton.classList.add('hidden')
      } else {
        nextButton.classList.remove('hidden')
      }
      nextButton.addEventListener('click', () => {
        swiperEl.swiper.slideNext()
      })
    }

    // Create progress line indicator
    if (progressBar) {
      // Create the inner progress line
      const progressLine = document.createElement('div')
      progressLine.className = 'progress-line'
      progressBar.appendChild(progressLine)

      // Initialize progress
      this.updateProgressBar(swiperEl.swiper, progressLine)

      // Update progress on slide change
      swiperEl.swiper.on('slideChange', () => {
        this.updateProgressBar(swiperEl.swiper, progressLine)
      })
    }
  }

  updateProgressBar(swiper, progressLine) {
    if (!progressLine) return

    const isMobile = window.innerWidth <= 768
    if (isMobile) {
      // Get the real slide index from the active slide's data attribute
      const activeSlide = swiper.slides[swiper.activeIndex]
      const realIndex = parseInt(activeSlide.getAttribute('data-slide-index'))
      // Find the highest slide index
      const slides = this.querySelectorAll('swiper-slide')
      let maxIndex = 0
      slides.forEach(slide => {
        const index = parseInt(slide.getAttribute('data-slide-index'))
        if (index > maxIndex) maxIndex = index
      })
      // Calculate progress based on real index and max index
      const progress = (realIndex / maxIndex) * 100
      progressLine.style.width = `${progress}%`
    } else {
      // Calculate progress based on active index
      const progress = swiper.progress * 100
      progressLine.style.width = `${progress}%`
    }
  }
}

if (!customElements.get('basis-carousel')) {
  customElements.define('basis-carousel', Carousel)
}
